<script setup lang="ts">
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import type { StoreEventPayload } from '@/types/events'
import MarkdownEditor from '@/components/common/MarkdownEditor.vue'
import AppSpinner from '@/components/shared/AppSpinner.vue'

interface EventFormData {
  name: string
  tagline: string
  description: string
  facebook_page: string
  instagram_page: string
  external_website_url: string
  mail_bcc: string
  contact_email: string
  maintenance: boolean
}

const props = defineProps<{
  modelValue: EventFormData
  loading: boolean
  errors: Record<string, string[]>
  isEdit?: boolean
  submitLabel?: string
}>()

const emit = defineEmits<{
  'update:modelValue': [value: EventFormData]
  submit: [data: StoreEventPayload]
  cancel: []
}>()

const { t } = useI18n()

const taglineRemaining = computed(() => 255 - (props.modelValue.tagline?.length ?? 0))

function update(field: keyof EventFormData, value: string | boolean): void {
  emit('update:modelValue', { ...props.modelValue, [field]: value })
}

function handleSubmit(): void {
  const payload: StoreEventPayload = {
    name: props.modelValue.name,
    tagline: props.modelValue.tagline.trim() || null,
    description: props.modelValue.description.trim() || null,
    facebook_page: props.modelValue.facebook_page || null,
    instagram_page: props.modelValue.instagram_page || null,
    external_website_url: props.modelValue.external_website_url || null,
    mail_bcc: props.modelValue.mail_bcc.trim() || null,
    contact_email: props.modelValue.contact_email.trim() || null,
    maintenance: props.modelValue.maintenance,
  }
  emit('submit', payload)
}
</script>

<template>
  <form novalidate class="space-y-6" @submit.prevent="handleSubmit">
    <!-- Name -->
    <div>
      <label for="event-name" class="block text-sm font-medium text-slate-300 mb-1">
        {{ t('events.form.name_label') }}
        <span class="text-red-400 ml-0.5">*</span>
      </label>
      <input
        id="event-name"
        :value="modelValue.name"
        type="text"
        :placeholder="t('events.form.name_placeholder')"
        class="w-full rounded-lg bg-slate-800 border px-4 py-2.5 text-white placeholder-slate-500 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 transition-colors"
        :class="errors['name'] ? 'border-red-500' : 'border-slate-700 focus:border-emerald-500'"
        @input="update('name', ($event.target as HTMLInputElement).value)"
      />
      <p v-if="errors['name']" class="mt-1 text-xs text-red-400">{{ errors['name'][0] }}</p>
    </div>

    <!-- Tagline -->
    <div>
      <label for="event-tagline" class="block text-sm font-medium text-slate-300 mb-1">
        {{ t('events.form.tagline_label') }}
      </label>
      <input
        id="event-tagline"
        :value="modelValue.tagline"
        type="text"
        maxlength="255"
        :placeholder="t('events.form.tagline_placeholder')"
        class="w-full rounded-lg bg-slate-800 border px-4 py-2.5 text-white placeholder-slate-500 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 transition-colors"
        :class="errors['tagline'] ? 'border-red-500' : 'border-slate-700 focus:border-emerald-500'"
        @input="update('tagline', ($event.target as HTMLInputElement).value)"
      />
      <div class="mt-1 flex items-start justify-between gap-4">
        <p class="text-xs text-slate-500">{{ t('events.form.tagline_hint') }}</p>
        <p class="text-xs text-slate-500 shrink-0">{{ taglineRemaining }}</p>
      </div>
      <p v-if="errors['tagline']" class="mt-1 text-xs text-red-400">{{ errors['tagline'][0] }}</p>
    </div>

    <!-- Description -->
    <div>
      <label for="event-description" class="block text-sm font-medium text-slate-300 mb-1">
        {{ t('events.form.description_label') }}
      </label>
      <MarkdownEditor
        :model-value="modelValue.description"
        :placeholder="t('events.form.description_placeholder')"
        :max-length="65535"
        input-id="event-description"
        @update:model-value="update('description', $event)"
      />
      <p class="mt-1 text-xs text-slate-500">{{ t('events.form.description_hint') }}</p>
      <p v-if="errors['description']" class="mt-1 text-xs text-red-400">{{ errors['description'][0] }}</p>
    </div>

    <!-- Facebook page -->
    <div>
      <label for="event-facebook" class="block text-sm font-medium text-slate-300 mb-1">
        {{ t('events.form.facebook_page_label') }}
      </label>
      <input
        id="event-facebook"
        :value="modelValue.facebook_page"
        type="url"
        :placeholder="t('events.form.facebook_page_placeholder')"
        class="w-full rounded-lg bg-slate-800 border px-4 py-2.5 text-white placeholder-slate-500 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 transition-colors"
        :class="errors['facebook_page'] ? 'border-red-500' : 'border-slate-700 focus:border-emerald-500'"
        @input="update('facebook_page', ($event.target as HTMLInputElement).value)"
      />
      <p v-if="errors['facebook_page']" class="mt-1 text-xs text-red-400">{{ errors['facebook_page'][0] }}</p>
    </div>

    <!-- Instagram page -->
    <div>
      <label for="event-instagram" class="block text-sm font-medium text-slate-300 mb-1">
        {{ t('events.form.instagram_page_label') }}
      </label>
      <input
        id="event-instagram"
        :value="modelValue.instagram_page"
        type="url"
        :placeholder="t('events.form.instagram_page_placeholder')"
        class="w-full rounded-lg bg-slate-800 border px-4 py-2.5 text-white placeholder-slate-500 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 transition-colors"
        :class="errors['instagram_page'] ? 'border-red-500' : 'border-slate-700 focus:border-emerald-500'"
        @input="update('instagram_page', ($event.target as HTMLInputElement).value)"
      />
      <p v-if="errors['instagram_page']" class="mt-1 text-xs text-red-400">{{ errors['instagram_page'][0] }}</p>
    </div>

    <!-- External website -->
    <div>
      <label for="event-external-website" class="block text-sm font-medium text-slate-300 mb-1">
        {{ t('events.form.external_website_url_label') }}
      </label>
      <input
        id="event-external-website"
        :value="modelValue.external_website_url"
        type="url"
        :placeholder="t('events.form.external_website_url_placeholder')"
        class="w-full rounded-lg bg-slate-800 border px-4 py-2.5 text-white placeholder-slate-500 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 transition-colors"
        :class="errors['external_website_url'] ? 'border-red-500' : 'border-slate-700 focus:border-emerald-500'"
        @input="update('external_website_url', ($event.target as HTMLInputElement).value)"
      />
      <p class="mt-1 text-xs text-slate-500">{{ t('events.form.external_website_url_hint') }}</p>
      <p v-if="errors['external_website_url']" class="mt-1 text-xs text-red-400">{{ errors['external_website_url'][0] }}</p>
    </div>

    <!-- Contact email -->
    <div>
      <label for="event-contact-email" class="block text-sm font-medium text-slate-300 mb-1">
        {{ t('events.form.contact_email_label') }}
      </label>
      <input
        id="event-contact-email"
        :value="modelValue.contact_email"
        type="email"
        maxlength="255"
        :placeholder="t('events.form.contact_email_placeholder')"
        class="w-full rounded-lg bg-slate-800 border px-4 py-2.5 text-white placeholder-slate-500 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 transition-colors"
        :class="errors['contact_email'] ? 'border-red-500' : 'border-slate-700 focus:border-emerald-500'"
        @input="update('contact_email', ($event.target as HTMLInputElement).value)"
      />
      <p class="mt-1 text-xs text-slate-500">{{ t('events.form.contact_email_hint') }}</p>
      <p v-if="errors['contact_email']" class="mt-1 text-xs text-red-400">{{ errors['contact_email'][0] }}</p>
    </div>

    <!-- BCC email -->
    <div>
      <label for="event-mail-bcc" class="block text-sm font-medium text-slate-300 mb-1">
        {{ t('events.form.mail_bcc_label') }}
      </label>
      <input
        id="event-mail-bcc"
        :value="modelValue.mail_bcc"
        type="text"
        inputmode="email"
        autocomplete="off"
        :placeholder="t('events.form.mail_bcc_placeholder')"
        class="w-full rounded-lg bg-slate-800 border px-4 py-2.5 text-white placeholder-slate-500 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 transition-colors"
        :class="errors['mail_bcc'] ? 'border-red-500' : 'border-slate-700 focus:border-emerald-500'"
        @input="update('mail_bcc', ($event.target as HTMLInputElement).value)"
      />
      <p class="mt-1 text-xs text-slate-500">{{ t('events.form.mail_bcc_hint') }}</p>
      <p v-if="errors['mail_bcc']" class="mt-1 text-xs text-red-400">{{ errors['mail_bcc'][0] }}</p>
    </div>

    <!-- Maintenance mode -->
    <div class="flex items-start gap-3 p-4 rounded-lg bg-slate-800/50 border border-slate-700">
      <input
        id="event-maintenance"
        :checked="modelValue.maintenance"
        type="checkbox"
        class="mt-0.5 w-4 h-4 rounded accent-emerald-500 cursor-pointer"
        @change="update('maintenance', ($event.target as HTMLInputElement).checked)"
      />
      <div>
        <label for="event-maintenance" class="text-sm font-medium text-slate-300 cursor-pointer">
          {{ t('events.form.maintenance_label') }}
        </label>
        <p class="text-xs text-slate-500 mt-0.5">{{ t('events.form.maintenance_hint') }}</p>
      </div>
    </div>

    <!-- Actions -->
    <div class="flex items-center gap-3 pt-2">
      <button
        type="submit"
        :disabled="loading"
        class="bg-emerald-500 hover:bg-emerald-400 disabled:opacity-50 disabled:cursor-not-allowed text-white font-semibold rounded-lg px-5 py-2.5 text-sm transition-colors flex items-center gap-2"
      >
        <AppSpinner v-if="loading" size="button" />
        {{ loading ? t('common.loading') : (submitLabel ?? (isEdit ? t('events.form.submit_update') : t('events.form.submit_create'))) }}
      </button>
      <button
        type="button"
        class="border border-slate-600 hover:border-slate-500 text-slate-300 hover:text-white font-semibold rounded-lg px-5 py-2.5 text-sm transition-colors"
        @click="emit('cancel')"
      >
        {{ t('events.form.cancel') }}
      </button>
    </div>
  </form>
</template>
